import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SECTIONS, type NavItem } from './navigation';
import { Icons } from './icons';
import { usePermission } from '../../hooks/usePermission';

/**
 * Jump anywhere by typing.
 *
 * Thirty screens is more than anybody wants to hunt through with a mouse, and
 * a shop's staff learn two or three routes and then get stuck when they need a
 * fourth. Typing what you want is faster than remembering which group somebody
 * filed it under — so the palette matches on plain words a shopkeeper would
 * use ("gold rate", "karigar", "stock take") as well as on the menu label.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { can } = usePermission();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const allowed = useMemo(
    () =>
      SECTIONS.flatMap((section) =>
        section.items
          .filter((item) => !item.permission || can(item.permission))
          .map((item) => ({ ...item, section: section.heading })),
      ),
    [can],
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allowed.slice(0, 8);
    return allowed
      .map((item) => {
        const label = item.label.toLowerCase();
        // A label that starts with what was typed is almost always the one
        // wanted, so it wins over a word buried in an alias.
        const score = label.startsWith(q)
          ? 0
          : label.includes(q)
            ? 1
            : (item.finds ?? []).some((f) => f.includes(q))
              ? 2
              : item.section.toLowerCase().includes(q)
                ? 3
                : -1;
        return { item, score };
      })
      .filter((m) => m.score >= 0)
      .sort((a, b) => a.score - b.score)
      .slice(0, 8)
      .map((m) => m.item);
  }, [query, allowed]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  useEffect(() => setCursor(0), [query]);

  if (!open) return null;

  const go = (item: NavItem): void => {
    navigate(item.to);
    onClose();
  };

  const onKey = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter' && matches[cursor]) {
      e.preventDefault();
      go(matches[cursor]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-rail/40 pt-[12vh]" onClick={onClose}>
      <div
        className="w-full max-w-lg overflow-hidden rounded-md bg-surface shadow-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-silver-100 px-3">
          <span className="text-ink-faint">
            <Icons.search />
          </span>
          <input
            ref={inputRef}
            className="w-full bg-transparent py-3 text-sm outline-none placeholder:text-ink-faint"
            placeholder="Where to? Try “gold rate”, “karigar”, “stock take”"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
          />
        </div>

        {matches.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-ink-soft">
            Nothing here by that name. Try what you would call it out loud.
          </p>
        ) : (
          <ul className="max-h-80 overflow-y-auto py-1">
            {matches.map((item, i) => {
              const Icon = Icons[item.icon];
              return (
                <li key={item.to + item.label}>
                  <button
                    className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm ${
                      i === cursor ? 'bg-gold-50 text-ink' : 'text-ink-soft hover:bg-silver-50'
                    }`}
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => go(item)}
                  >
                    <span className={i === cursor ? 'text-gold-600' : 'text-ink-faint'}>
                      <Icon />
                    </span>
                    <span className="font-medium text-ink">{item.label}</span>
                    <span className="ml-auto text-2xs text-ink-faint">{item.section}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex items-center gap-3 border-t border-silver-100 bg-silver-50 px-3 py-1.5 text-2xs text-ink-faint">
          <span>↑↓ to move</span>
          <span>↵ to open</span>
          <span>esc to close</span>
        </div>
      </div>
    </div>
  );
}
